/**
 * InvoiceWidget
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 *
 * LOGICA BUSINESS:
 *
 * FATTURE EMESSE (direction: 'issued'):
 * - Bozza (draft): completamente modificabile
 * - Inviata/Pagata/Scaduta: SOLA LETTURA con avviso
 *   - Pulsante "Modifica comunque" con alert di warning
 *   - Alla modifica, conferma che la fattura è stata ri-contabilizzata dal ricevente
 *
 * FATTURE RICEVUTE (direction: 'received'):
 * - Non modificabile (è del fornitore)
 * - Può solo:
 *   - Essere associata ad una transazione bancaria
 *   - Avere tag
 *   - "Aggiungi a fornitori" se mittente non in rubrica
 */

import { useState } from 'react';
import {
  Plus,
  Trash2,
  FileText,
  AlertTriangle,
  Lock,
  Unlock,
  Link2,
  Tag,
  UserPlus,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Ban,
  Send,
  Edit3,
  Building,
  MessageSquareWarning,
  Phone,
  Mail,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { useHubStore } from '@business-assistant/shared';
import { useInvoices, useMovements } from '@business-assistant/shared/hooks';
import type { InvoiceItem, InvoiceStatus, InvoiceDirection } from '@business-assistant/shared/domain';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import { Button } from '../common/forms';

// =============================================================================
// TYPES
// =============================================================================

interface InvoiceWidgetProps {
  invoiceId?: string;
  projectId?: string;
  onClose: () => void;
  onSave: (data: InvoiceFormData) => void;
}

export interface InvoiceFormData {
  number: string;
  direction: InvoiceDirection;
  projectId?: string;
  contactId?: string;
  contactName: string;
  issueDate: string;
  dueDate: string;
  status: InvoiceStatus;
  items: InvoiceItem[];
  notes?: string;
  tags?: string[];
  linkedTransactionId?: string;
}

// =============================================================================
// STATUS HELPERS
// =============================================================================

const STATUS_CONFIG: Record<InvoiceStatus, { labelKey: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  draft: { labelKey: 'invoices.status.draft', color: '#6b7280', bgColor: '#f3f4f6', icon: <Edit3 size={14} /> },
  sent: { labelKey: 'invoices.status.sent', color: '#3b82f6', bgColor: '#dbeafe', icon: <Send size={14} /> },
  paid: { labelKey: 'invoices.status.paid', color: '#22c55e', bgColor: '#dcfce7', icon: <CheckCircle2 size={14} /> },
  overdue: { labelKey: 'invoices.status.overdue', color: '#ef4444', bgColor: '#fee2e2', icon: <Clock size={14} /> },
  cancelled: { labelKey: 'invoices.status.cancelled', color: '#9ca3af', bgColor: '#f3f4f6', icon: <Ban size={14} /> },
};

// =============================================================================
// ISSUED INVOICE VIEW (Fattura Emessa)
// =============================================================================

function IssuedInvoiceView({
  invoice,
  onClose,
  onSave,
}: {
  invoice: any;
  onClose: () => void;
  onSave: (data: InvoiceFormData) => void;
}) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const isEditable = invoice.status === 'draft';
  const [editMode, setEditMode] = useState(isEditable);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [showReaccountConfirm, setShowReaccountConfirm] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<InvoiceFormData | null>(null);

  const [formData, setFormData] = useState<InvoiceFormData>({
    number: invoice.number || '',
    direction: 'issued',
    projectId: invoice.projectId,
    contactId: invoice.contactId || invoice.customerId,
    contactName: invoice.contactName || invoice.customerName || '',
    issueDate: invoice.issueDate || invoice.date || invoice.at || new Date().toISOString().split('T')[0],
    dueDate: invoice.dueDate || invoice.dueAt || '',
    status: invoice.status || 'draft',
    items: invoice.items || [{ description: '', quantity: 1, unitPrice: 0, vatRate: 22, total: 0 }],
    notes: invoice.notes,
    tags: invoice.tags || [],
  });

  const statusConfig = STATUS_CONFIG[formData.status];

  // Calcola totali
  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
    const tax = subtotal * 0.22;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const totals = calculateTotals();

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : newItems[index].quantity;
      const unitPrice = field === 'unitPrice' ? value : newItems[index].unitPrice;
      newItems[index].total = quantity * unitPrice;
    }
    setFormData({ ...formData, items: newItems });
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unitPrice: 0, vatRate: 22, total: 0 }],
    });
  };

  const handleRemoveItem = (index: number) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index),
      });
    }
  };

  const handleUnlockEdit = () => {
    setShowEditWarning(true);
  };

  const confirmUnlockEdit = () => {
    setShowEditWarning(false);
    setEditMode(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Se la fattura era già inviata e stiamo modificando, chiedi conferma ri-contabilizzazione
    if (!isEditable && editMode) {
      setPendingFormData(formData);
      setShowReaccountConfirm(true);
    } else {
      onSave(formData);
    }
  };

  const confirmReaccount = () => {
    if (pendingFormData) {
      onSave(pendingFormData);
    }
    setShowReaccountConfirm(false);
  };

  return (
    <DetailPanel
      title={invoice.id ? t('invoices.invoiceNumber', { number: formData.number || t('invoices.notAvailable') }) : t('invoices.newInvoice')}
      subtitle={t('invoices.issuedInvoice')}
      icon={
        <div style={{ position: 'relative' }}>
          <FileText size={20} style={{ color: '#F59E0B' }} />
          <ArrowUpRight size={10} style={{ position: 'absolute', bottom: -2, right: -2, color: '#22c55e' }} />
        </div>
      }
      onClose={onClose}
      accentColor="#F59E0B"
      width="50%"
    >
      {/* Warning Modal: Sblocco Modifica */}
      {showEditWarning && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalIcon}>
              <AlertTriangle size={32} style={{ color: '#f59e0b' }} />
            </div>
            <h3 style={styles.modalTitle}>{t('invoices.warningAlreadySent')}</h3>
            <p style={styles.modalText}>
              {t('invoices.warningAlreadySentText')}
            </p>
            <p style={styles.modalText}>
              {t('invoices.warningAlreadySentText2')}
            </p>
            <div style={styles.modalActions}>
              <button onClick={() => setShowEditWarning(false)} style={styles.modalBtnCancel}>
                {t('common.cancel')}
              </button>
              <button onClick={confirmUnlockEdit} style={styles.modalBtnConfirm}>
                <Unlock size={16} /> {t('invoices.unlockAndEdit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal: Ri-contabilizzazione */}
      {showReaccountConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalIcon}>
              <CheckCircle2 size={32} style={{ color: '#22c55e' }} />
            </div>
            <h3 style={styles.modalTitle}>{t('invoices.confirmReaccount')}</h3>
            <p style={styles.modalText}>
              {t('invoices.confirmReaccountText')}
            </p>
            <ul style={styles.modalList}>
              <li>{t('invoices.confirmReaccountList1')}</li>
              <li>{t('invoices.confirmReaccountList2')}</li>
              <li>{t('invoices.confirmReaccountList3')}</li>
            </ul>
            <div style={styles.modalActions}>
              <button onClick={() => setShowReaccountConfirm(false)} style={styles.modalBtnCancel}>
                {t('invoices.goBack')}
              </button>
              <button onClick={confirmReaccount} style={styles.modalBtnConfirm}>
                <CheckCircle2 size={16} /> {t('invoices.confirmSaveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Status Banner */}
        <div style={{
          ...styles.statusBanner,
          background: statusConfig.bgColor,
          borderColor: statusConfig.color,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: statusConfig.color }}>
            {statusConfig.icon}
            <span style={{ fontWeight: 600 }}>{t(statusConfig.labelKey)}</span>
          </div>
          {!isEditable && !editMode && (
            <button type="button" onClick={handleUnlockEdit} style={styles.unlockBtn}>
              <Lock size={14} /> {t('invoices.editAnyway')}
            </button>
          )}
          {!isEditable && editMode && (
            <span style={{ fontSize: '0.75rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Unlock size={12} /> {t('invoices.unlockedForEdit')}
            </span>
          )}
        </div>

        {/* Invoice Details */}
        <DetailSection title={t('invoices.invoiceData')}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={styles.label}>{t('invoices.numberRequired')}</label>
              <input
                type="text"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                disabled={!editMode}
                required
                placeholder="2024-001"
                style={{ ...styles.input, ...(editMode ? {} : styles.inputDisabled) }}
              />
            </div>
            <div>
              <label style={styles.label}>{t('invoices.customerRequired')}</label>
              <input
                type="text"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                disabled={!editMode}
                required
                placeholder={t('invoices.customerPlaceholder')}
                style={{ ...styles.input, ...(editMode ? {} : styles.inputDisabled) }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={styles.label}>{t('invoices.issueDateRequired')}</label>
              <input
                type="date"
                value={formData.issueDate}
                onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                disabled={!editMode}
                required
                style={{ ...styles.input, ...(editMode ? {} : styles.inputDisabled) }}
              />
            </div>
            <div>
              <label style={styles.label}>{t('invoices.dueDate')}</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                disabled={!editMode}
                style={{ ...styles.input, ...(editMode ? {} : styles.inputDisabled) }}
              />
            </div>
            <div>
              <label style={styles.label}>{t('invoices.statusLabel')}</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as InvoiceStatus })}
                disabled={!editMode}
                style={{ ...styles.select, ...(editMode ? {} : styles.inputDisabled) }}
              >
                <option value="draft">{t('invoices.status.draft')}</option>
                <option value="sent">{t('invoices.status.sent')}</option>
                <option value="paid">{t('invoices.status.paid')}</option>
                <option value="overdue">{t('invoices.status.overdue')}</option>
                <option value="cancelled">{t('invoices.status.cancelled')}</option>
              </select>
            </div>
          </div>
        </DetailSection>

        {/* Items */}
        <DetailSection title={t('invoices.invoiceItems')}>
          {formData.items.map((item, index) => (
            <div key={index} style={styles.itemRow}>
              <input
                type="text"
                value={item.description}
                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                disabled={!editMode}
                placeholder={t('invoices.description')}
                required
                style={{ ...styles.itemInput, flex: 2, ...(editMode ? {} : styles.inputDisabled) }}
              />
              <input
                type="number"
                min="1"
                value={item.quantity}
                onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                disabled={!editMode}
                placeholder={t('invoices.quantity')}
                style={{ ...styles.itemInput, width: '60px', ...(editMode ? {} : styles.inputDisabled) }}
              />
              <input
                type="number"
                min="0"
                step="0.01"
                value={item.unitPrice}
                onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                disabled={!editMode}
                placeholder="€"
                style={{ ...styles.itemInput, width: '80px', ...(editMode ? {} : styles.inputDisabled) }}
              />
              <span style={styles.itemTotal}>€ {(item.total || 0).toFixed(2)}</span>
              {editMode && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  disabled={formData.items.length === 1}
                  style={styles.removeBtn}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}

          {editMode && (
            <button type="button" onClick={handleAddItem} style={styles.addItemBtn}>
              <Plus size={16} /> {t('invoices.addItem')}
            </button>
          )}
        </DetailSection>

        {/* Totals */}
        <DetailSection title={t('invoices.summary')}>
          <div style={styles.totalsBox}>
            <div style={styles.totalRow}>
              <span>{t('invoices.subtotal')}</span>
              <span>€ {totals.subtotal.toFixed(2)}</span>
            </div>
            <div style={styles.totalRow}>
              <span>{t('invoices.vat')}</span>
              <span>€ {totals.tax.toFixed(2)}</span>
            </div>
            <div style={styles.totalRowFinal}>
              <span>{t('invoices.total')}</span>
              <span>€ {totals.total.toFixed(2)}</span>
            </div>
          </div>
        </DetailSection>

        {/* Notes */}
        <DetailSection title={t('invoices.notes')}>
          <textarea
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            disabled={!editMode}
            placeholder={t('invoices.notesPlaceholder')}
            rows={2}
            style={{ ...styles.textarea, ...(editMode ? {} : styles.inputDisabled) }}
          />
        </DetailSection>

        {/* Actions */}
        <DetailActions>
          <Button variant="secondary" type="button" onClick={onClose}>
            {t('invoices.close')}
          </Button>
          {editMode && (
            <Button variant="primary" type="submit">
              {invoice.id ? t('invoices.saveChanges') : t('invoices.createInvoice')}
            </Button>
          )}
        </DetailActions>
      </form>
    </DetailPanel>
  );
}

// =============================================================================
// RECEIVED INVOICE VIEW (Fattura Ricevuta)
// =============================================================================

interface ContestEmailData {
  to: string;
  subject: string;
  body: string;
  attachInvoice: boolean;
}

function ReceivedInvoiceView({
  invoice,
  onClose,
  onSave,
}: {
  invoice: any;
  onClose: () => void;
  onSave: (data: InvoiceFormData) => void;
}) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const { contacts, createContact } = useHubStore();
  const { data: rqMovements = [] } = useMovements();
  const movementsData = rqMovements;

  const [linkedTransactionId, setLinkedTransactionId] = useState(invoice.linkedTransactionId || '');
  const [tags, setTags] = useState<string[]>(invoice.tags || []);
  const [newTag, setNewTag] = useState('');
  const [showAddSupplier, setShowAddSupplier] = useState(false);

  // Contest invoice state
  const [showContestModal, setShowContestModal] = useState(false);
  const [showNoEmailModal, setShowNoEmailModal] = useState(false);
  const [contestLoading, setContestLoading] = useState(false);
  const [contestEmail, setContestEmail] = useState<ContestEmailData | null>(null);
  const [showEmailComposer, setShowEmailComposer] = useState(false);

  // Extract contact info from invoice
  const supplierName = invoice.contactName || invoice.customerName || invoice.counterparty || '';
  const supplierEmail = invoice.supplierEmail || invoice.email || '';
  const supplierPhone = invoice.supplierPhone || invoice.phone || '';
  const supplierVAT = invoice.supplierVAT || invoice.vatNumber || '';

  // Check if supplier exists in contacts
  const supplierExists = contacts.some(
    c => c.displayName.toLowerCase() === supplierName.toLowerCase() ||
         c.company?.toLowerCase() === supplierName.toLowerCase()
  );

  const statusConfig = STATUS_CONFIG[invoice.status || 'sent'];

  // Calcola totali
  const calculateTotals = () => {
    const items = invoice.items || [];
    const subtotal = items.reduce((sum: number, item: InvoiceItem) => sum + (item.total || 0), 0);
    const tax = subtotal * 0.22;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const totals = calculateTotals();

  // ==========================================================================
  // CONTEST INVOICE FUNCTIONALITY
  // ==========================================================================

  const handleContestClick = async () => {
    // Check if we have email
    if (!supplierEmail) {
      // No email - check if we have phone
      if (supplierPhone) {
        setShowNoEmailModal(true);
      } else {
        // No email and no phone - show composer anyway with empty recipient
        await generateContestEmail('');
      }
      return;
    }

    // We have email - generate AI content
    await generateContestEmail(supplierEmail);
  };

  const generateContestEmail = async (toEmail: string) => {
    setContestLoading(true);
    setShowContestModal(true);

    try {
      // Build invoice context for AI
      const invoiceContext = `
Invoice to dispute:
- Number: ${invoice.number || 'N/A'}
- Supplier: ${supplierName || 'N/A'}
- VAT ID: ${supplierVAT || 'N/A'}
- Date: ${invoice.issueDate || invoice.date || invoice.at || 'N/A'}
- Total amount: € ${totals.total.toFixed(2)}
- Items:
${(invoice.items || []).map((item: InvoiceItem) => `  • ${item.description}: ${item.quantity}x € ${item.unitPrice} = € ${item.total}`).join('\n')}
${invoice.notes ? `- Notes: ${invoice.notes}` : ''}
      `.trim();

      // Use fallback template
      const generatedBody = 'Gentile Fornitore,\n\nDesidero sottoporre alla Vostra attenzione alcune perplessità riguardo alla fattura in oggetto.\n\nResto in attesa di un Vostro cortese riscontro.\n\nCordiali saluti';

      setContestEmail({
        to: toEmail,
        subject: `Clarification request - Invoice ${invoice.number || 'N/A'}`,
        body: generatedBody,
        attachInvoice: true,
      });
    } catch (error) {
      console.error('Error generating contest email:', error);
      // Fallback to default text
      setContestEmail({
        to: toEmail,
        subject: `Inquiry regarding Invoice ${invoice.number || 'N/A'}`,
        body: `Dear ${supplierName || 'Supplier'},

Regarding invoice no. ${invoice.number || 'N/A'} dated ${invoice.issueDate || invoice.date ? new Date(invoice.issueDate || invoice.date).toLocaleDateString(undefined) : 'N/A'} for € ${totals.total.toFixed(2)}, I would like to bring to your attention some questions regarding the invoiced amounts.

I kindly request clarification on this matter.

I look forward to your response.

Best regards`,
        attachInvoice: true,
      });
    } finally {
      setContestLoading(false);
      setShowEmailComposer(true);
    }
  };

  const handlePrepareEmailAnyway = async () => {
    setShowNoEmailModal(false);
    await generateContestEmail('');
  };

  const handleCall = () => {
    setShowNoEmailModal(false);
    // Open phone dialer
    window.location.href = `tel:${supplierPhone}`;
  };

  const handleSendContestEmail = () => {
    if (contestEmail) {
      // In a real app, this would send via email service
      // For now, open mailto link
      const mailtoUrl = `mailto:${contestEmail.to}?subject=${encodeURIComponent(contestEmail.subject)}&body=${encodeURIComponent(contestEmail.body)}`;
      window.open(mailtoUrl, '_blank');
      setShowContestModal(false);
      setShowEmailComposer(false);
      setContestEmail(null);
    }
  };

  // ==========================================================================
  // OTHER HANDLERS
  // ==========================================================================

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleAddSupplier = () => {
    createContact({
      displayName: supplierName,
      company: supplierName,
      handles: {
        email: supplierEmail || undefined,
        phone: supplierPhone || undefined,
      },
      tags: ['fornitore'],
      role: 'Fornitore',
    });
    setShowAddSupplier(false);
  };

  const handleSave = () => {
    onSave({
      ...invoice,
      direction: 'received',
      linkedTransactionId,
      tags,
    });
  };

  return (
    <DetailPanel
      title={t('invoices.invoiceNumber', { number: invoice.number || t('invoices.notAvailable') })}
      subtitle={t('invoices.receivedInvoice')}
      icon={
        <div style={{ position: 'relative' }}>
          <FileText size={20} style={{ color: '#ef4444' }} />
          <ArrowDownLeft size={10} style={{ position: 'absolute', bottom: -2, right: -2, color: '#ef4444' }} />
        </div>
      }
      onClose={onClose}
      accentColor="#ef4444"
      width="50%"
    >
      {/* Status Banner with Contest Button */}
      <div style={{
        ...styles.statusBanner,
        background: statusConfig.bgColor,
        borderColor: statusConfig.color,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: statusConfig.color }}>
          {statusConfig.icon}
          <span style={{ fontWeight: 600 }}>{t(statusConfig.labelKey)}</span>
          <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>
            {t('invoices.supplierDocument')}
          </span>
        </div>
        <button
          type="button"
          onClick={handleContestClick}
          style={styles.contestBtn}
        >
          <MessageSquareWarning size={14} /> {t('invoices.contestInvoice')}
        </button>
      </div>

      {/* No Email Modal - Phone Available */}
      {showNoEmailModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalIcon}>
              <AlertTriangle size={32} style={{ color: '#f59e0b' }} />
            </div>
            <h3 style={styles.modalTitle}>{t('invoices.emailNotAvailable')}</h3>
            <p style={styles.modalText}>
              {t('invoices.emailNotAvailableText')}
            </p>
            {supplierPhone && (
              <p style={styles.modalText}>
                {t('invoices.phoneAvailable', { phone: supplierPhone })}
              </p>
            )}
            <div style={styles.modalActions}>
              <button onClick={() => setShowNoEmailModal(false)} style={styles.modalBtnCancel}>
                {t('common.cancel')}
              </button>
              {supplierPhone && (
                <button onClick={handleCall} style={styles.modalBtnCall}>
                  <Phone size={16} /> {t('invoices.call')}
                </button>
              )}
              <button onClick={handlePrepareEmailAnyway} style={styles.modalBtnConfirm}>
                <Mail size={16} /> {t('invoices.prepareEmail')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contest Email Modal */}
      {showContestModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modalContent, maxWidth: '600px', textAlign: 'left' }}>
            {contestLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader2 size={40} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '1rem', color: '#6b7280' }}>
                  <Sparkles size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  {t('invoices.aiGenerating')}
                </p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : showEmailComposer && contestEmail ? (
              <>
                <h3 style={{ ...styles.modalTitle, textAlign: 'left', marginBottom: '1.5rem' }}>
                  <Mail size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  {t('invoices.contestInvoiceTitle')}
                </h3>

                {/* To Field */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={styles.label}>{t('invoices.recipient')}</label>
                  <input
                    type="email"
                    value={contestEmail.to}
                    onChange={(e) => setContestEmail({ ...contestEmail, to: e.target.value })}
                    placeholder={t('invoices.recipientPlaceholder')}
                    style={styles.input}
                  />
                  {!contestEmail.to && (
                    <span style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: '0.25rem', display: 'block' }}>
                      ⚠️ {t('invoices.enterSupplierEmail')}
                    </span>
                  )}
                </div>

                {/* Subject Field */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={styles.label}>{t('invoices.subjectLabel')}</label>
                  <input
                    type="text"
                    value={contestEmail.subject}
                    onChange={(e) => setContestEmail({ ...contestEmail, subject: e.target.value })}
                    style={styles.input}
                  />
                </div>

                {/* Body Field */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={styles.label}>
                    {t('invoices.messageLabel')}
                    <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '0.5rem', fontSize: '0.75rem' }}>
                      <Sparkles size={12} style={{ display: 'inline', marginRight: '0.25rem' }} />
                      {t('invoices.aiGenerated')}
                    </span>
                  </label>
                  <textarea
                    value={contestEmail.body}
                    onChange={(e) => setContestEmail({ ...contestEmail, body: e.target.value })}
                    rows={10}
                    style={{ ...styles.textarea, minHeight: '200px' }}
                  />
                </div>

                {/* Attachment Toggle */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={contestEmail.attachInvoice}
                      onChange={(e) => setContestEmail({ ...contestEmail, attachInvoice: e.target.checked })}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '0.875rem' }}>{t('invoices.attachInvoiceCopy')}</span>
                  </label>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowContestModal(false);
                      setShowEmailComposer(false);
                      setContestEmail(null);
                    }}
                    style={styles.modalBtnCancel}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSendContestEmail}
                    disabled={!contestEmail.to}
                    style={{
                      ...styles.modalBtnConfirm,
                      background: contestEmail.to ? '#ef4444' : '#d1d5db',
                      cursor: contestEmail.to ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <Send size={16} /> {t('invoices.sendContest')}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Supplier Info + Add Button */}
      <DetailSection title={t('invoices.supplier')}>
        <div style={styles.supplierCard}>
          <div style={styles.supplierAvatar}>
            <Building size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '1rem' }}>{supplierName || t('invoices.notAvailable')}</div>
            {supplierVAT && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{t('invoices.vatNumber')} {supplierVAT}</div>
            )}
            {(supplierEmail || supplierPhone) && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', display: 'flex', gap: '0.75rem' }}>
                {supplierEmail && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Mail size={12} /> {supplierEmail}
                  </span>
                )}
                {supplierPhone && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Phone size={12} /> {supplierPhone}
                  </span>
                )}
              </div>
            )}
          </div>
          {!supplierExists && (
            <button
              type="button"
              onClick={() => setShowAddSupplier(true)}
              style={styles.addSupplierBtn}
            >
              <UserPlus size={14} /> {t('invoices.addToSuppliers')}
            </button>
          )}
          {supplierExists && (
            <span style={styles.supplierExistsBadge}>
              <CheckCircle2 size={12} /> {t('invoices.inAddressBook')}
            </span>
          )}
        </div>
      </DetailSection>

      {/* Add Supplier Modal */}
      {showAddSupplier && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalIcon}>
              <UserPlus size={32} style={{ color: '#3b82f6' }} />
            </div>
            <h3 style={styles.modalTitle}>{t('invoices.addSupplier')}</h3>
            <p style={styles.modalText}>
              {t('invoices.addSupplierText', { name: supplierName })}
            </p>
            {(supplierEmail || supplierPhone) && (
              <p style={{ ...styles.modalText, fontSize: '0.8125rem' }}>
                {t('invoices.detectedContacts')}
                {supplierEmail && <><br />📧 {supplierEmail}</>}
                {supplierPhone && <><br />📞 {supplierPhone}</>}
              </p>
            )}
            <div style={styles.modalActions}>
              <button onClick={() => setShowAddSupplier(false)} style={styles.modalBtnCancel}>
                {t('common.cancel')}
              </button>
              <button onClick={handleAddSupplier} style={styles.modalBtnConfirm}>
                <UserPlus size={16} /> {t('invoices.addSupplier')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Details (Read-only) */}
      <DetailSection title={t('invoices.invoiceDetails')}>
        <div style={styles.detailGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>{t('invoices.number')}</span>
            <span style={styles.detailValue}>{invoice.number || t('invoices.notAvailable')}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>{t('invoices.date')}</span>
            <span style={styles.detailValue}>
              {invoice.issueDate || invoice.date || invoice.at
                ? new Date(invoice.issueDate || invoice.date || invoice.at).toLocaleDateString(locale)
                : t('invoices.notAvailable')}
            </span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>{t('invoices.dueDate')}</span>
            <span style={styles.detailValue}>
              {invoice.dueDate || invoice.dueAt
                ? new Date(invoice.dueDate || invoice.dueAt).toLocaleDateString(locale)
                : t('invoices.notAvailable')}
            </span>
          </div>
        </div>
      </DetailSection>

      {/* Items (Read-only) */}
      {invoice.items && invoice.items.length > 0 && (
        <DetailSection title={t('invoices.items')}>
          {invoice.items.map((item: InvoiceItem, index: number) => (
            <div key={index} style={styles.readOnlyItem}>
              <span style={{ flex: 2 }}>{item.description}</span>
              <span style={{ width: '50px', textAlign: 'center' }}>{item.quantity}x</span>
              <span style={{ width: '80px', textAlign: 'right' }}>€ {item.unitPrice.toFixed(2)}</span>
              <span style={{ width: '80px', textAlign: 'right', fontWeight: 600 }}>€ {(item.total || 0).toFixed(2)}</span>
            </div>
          ))}
        </DetailSection>
      )}

      {/* Totals */}
      <DetailSection title={t('invoices.amounts')}>
        <div style={styles.totalsBox}>
          <div style={styles.totalRow}>
            <span>{t('invoices.subtotal')}</span>
            <span>€ {totals.subtotal.toFixed(2)}</span>
          </div>
          <div style={styles.totalRow}>
            <span>{t('invoices.vat')}</span>
            <span>€ {totals.tax.toFixed(2)}</span>
          </div>
          <div style={{ ...styles.totalRowFinal, color: '#ef4444' }}>
            <span>{t('invoices.toPay')}</span>
            <span>€ {totals.total.toFixed(2)}</span>
          </div>
        </div>
      </DetailSection>

      {/* Link Transaction */}
      <DetailSection title={t('invoices.linkBankTransaction')}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Link2 size={16} style={{ color: '#6b7280' }} />
          <select
            value={linkedTransactionId}
            onChange={(e) => setLinkedTransactionId(e.target.value)}
            style={styles.select}
          >
            <option value="">{t('invoices.selectTransaction')}</option>
            {movementsData
              .filter(m => m.direction === 'out') // Solo uscite per fatture ricevute
              .slice(0, 20)
              .map(m => (
                <option key={m.id} value={m.id}>
                  {new Date(m.at).toLocaleDateString(locale)} - € {Math.abs(m.amount / 100).toFixed(2)} - {m.description}
                </option>
              ))}
          </select>
        </div>
        {linkedTransactionId && (
          <div style={styles.linkedBadge}>
            <CheckCircle2 size={14} style={{ color: '#22c55e' }} />
            {t('invoices.transactionLinked')}
          </div>
        )}
      </DetailSection>

      {/* Tags */}
      <DetailSection title={t('invoices.tags')}>
        <div style={styles.tagsContainer}>
          {tags.map((tag, index) => (
            <span key={index} style={styles.tag}>
              <Tag size={12} />
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                style={styles.tagRemove}
              >
                ×
              </button>
            </span>
          ))}
          <div style={styles.addTagRow}>
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              placeholder={t('invoices.newTagPlaceholder')}
              style={styles.tagInput}
            />
            <button type="button" onClick={handleAddTag} style={styles.addTagBtn}>
              <Plus size={14} />
            </button>
          </div>
        </div>
      </DetailSection>

      {/* Notes */}
      {invoice.notes && (
        <DetailSection title={t('invoices.notes')}>
          <div style={styles.notesReadOnly}>{invoice.notes}</div>
        </DetailSection>
      )}

      {/* Actions */}
      <DetailActions>
        <Button variant="secondary" onClick={onClose}>
          {t('invoices.close')}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {t('invoices.saveChanges')}
        </Button>
      </DetailActions>
    </DetailPanel>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function InvoiceWidget({ invoiceId, projectId, onClose, onSave }: InvoiceWidgetProps) {
  const { data: invoices = [] } = useInvoices();
  const existingInvoice = invoiceId ? invoices.find(i => i.id === invoiceId) : null;

  // Determina la direzione
  const direction = existingInvoice?.direction || 'issued';

  // Se è una fattura ricevuta, mostra la vista read-only con associazione
  if (direction === 'received' && existingInvoice) {
    return (
      <ReceivedInvoiceView
        invoice={existingInvoice}
        onClose={onClose}
        onSave={onSave}
      />
    );
  }

  // Altrimenti, mostra la vista per fatture emesse (modificabile se bozza)
  return (
    <IssuedInvoiceView
      invoice={existingInvoice || { projectId, direction: 'issued', status: 'draft' }}
      onClose={onClose}
      onSave={onSave}
    />
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  label: {
    display: 'block',
    fontWeight: 500,
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
  },
  input: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
  },
  inputDisabled: {
    background: '#f9fafb',
    color: '#6b7280',
    cursor: 'not-allowed',
  },
  select: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    background: 'white',
  },
  textarea: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  statusBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    border: '1px solid',
  },
  unlockBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.375rem 0.75rem',
    border: 'none',
    borderRadius: '0.375rem',
    background: '#fef3c7',
    color: '#b45309',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  itemRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    alignItems: 'center',
  },
  itemInput: {
    padding: '0.5rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
  },
  itemTotal: {
    width: '80px',
    textAlign: 'right',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  removeBtn: {
    padding: '0.5rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    background: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ef4444',
  },
  addItemBtn: {
    padding: '0.5rem 0.75rem',
    border: '1px dashed var(--brand)',
    borderRadius: '0.375rem',
    background: 'white',
    color: 'var(--brand)',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
  },
  totalsBox: {
    padding: '1rem',
    background: 'var(--bg)',
    borderRadius: '0.5rem',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
  },
  totalRowFinal: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: '0.5rem',
    borderTop: '1px solid var(--line)',
    fontWeight: 600,
    fontSize: '1rem',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: 'white',
    borderRadius: '1rem',
    padding: '2rem',
    maxWidth: '420px',
    width: '90%',
    textAlign: 'center',
  },
  modalIcon: {
    marginBottom: '1rem',
  },
  modalTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  modalText: {
    margin: '0 0 0.75rem 0',
    fontSize: '0.9375rem',
    color: '#4b5563',
    lineHeight: 1.5,
  },
  modalList: {
    textAlign: 'left',
    margin: '1rem 0',
    paddingLeft: '1.5rem',
    color: '#4b5563',
    fontSize: '0.875rem',
    lineHeight: 1.8,
  },
  modalActions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center',
    marginTop: '1.5rem',
  },
  modalBtnCancel: {
    padding: '0.625rem 1.25rem',
    border: '1px solid var(--line)',
    borderRadius: '0.5rem',
    background: 'white',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  modalBtnConfirm: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.625rem 1.25rem',
    border: 'none',
    borderRadius: '0.5rem',
    background: '#3b82f6',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  modalBtnCall: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.625rem 1.25rem',
    border: 'none',
    borderRadius: '0.5rem',
    background: '#22c55e',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  contestBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.375rem 0.75rem',
    border: 'none',
    borderRadius: '0.375rem',
    background: '#fef2f2',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  // Received invoice styles
  supplierCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    background: '#fef2f2',
    borderRadius: '0.5rem',
    border: '1px solid #fecaca',
  },
  supplierAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#ef4444',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addSupplierBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.75rem',
    border: 'none',
    borderRadius: '0.375rem',
    background: '#3b82f6',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  supplierExistsBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.375rem 0.75rem',
    background: '#dcfce7',
    color: '#22c55e',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  detailLabel: {
    fontSize: '0.75rem',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailValue: {
    fontSize: '0.9375rem',
    fontWeight: 500,
  },
  readOnlyItem: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '0.875rem',
    alignItems: 'center',
  },
  linkedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.5rem',
    padding: '0.5rem 0.75rem',
    background: '#dcfce7',
    borderRadius: '0.375rem',
    fontSize: '0.8125rem',
    color: '#166534',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    alignItems: 'center',
  },
  tag: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.375rem 0.625rem',
    background: '#e0e7ff',
    color: '#4338ca',
    borderRadius: '9999px',
    fontSize: '0.8125rem',
  },
  tagRemove: {
    background: 'none',
    border: 'none',
    color: '#4338ca',
    cursor: 'pointer',
    padding: 0,
    marginLeft: '0.25rem',
    fontSize: '1rem',
    lineHeight: 1,
  },
  addTagRow: {
    display: 'flex',
    gap: '0.25rem',
  },
  tagInput: {
    padding: '0.375rem 0.5rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.8125rem',
    width: '100px',
  },
  addTagBtn: {
    padding: '0.375rem',
    border: '1px solid var(--brand)',
    borderRadius: '0.375rem',
    background: 'white',
    color: 'var(--brand)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  notesReadOnly: {
    padding: '0.75rem',
    background: '#f9fafb',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    color: '#4b5563',
    whiteSpace: 'pre-wrap',
  },
};
